/**
 * The global create — ONE offer, one route, one dialog, wherever it is pressed.
 *
 * There are four doors onto "make a new X" in this product: the shell's `New`
 * menu, ⌘K's `Create` group, the sidebar's `+` beside Saved views, and a
 * screen's own gold primary. `UI-PRIMITIVES.md` fixes the *vocabulary* those
 * doors offer (`CREATE_ACTIONS` in `components/ui/CreateMenu`); this module
 * fixes what pressing one of them actually does, so the four cannot drift into
 * doing three different things with the same word on them.
 *
 * The mechanism: a pick broadcasts a cancelable `af:create`, and the surface
 * that owns the kind claims it with `preventDefault()` (see `useCreateRequest`).
 * If nothing claims it — because that surface is not mounted — the caller
 * navigates to the surface that owns the kind and asks again once it is on
 * screen. So the offer always ends in the same composer, from anywhere.
 *
 * It used to live inside `AppShell` with `announceCreate` exported to nobody and
 * NO listeners anywhere in the product, which meant four of the five verbs on
 * the `New` menu quietly resolved to a navigation and then nothing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { CreateKind } from '@/components/ui';
import { ROUTES } from '@/lib/constants';

export const CREATE_EVENT = 'af:create';

/** How long to keep offering a create to a screen that is still downloading. */
const RETRY_MS = 3000;
const RETRY_STEP_MS = 60;

export interface CreateEventDetail {
  kind: CreateKind;
}

/** Which surface owns each kind. */
export const CREATE_ROUTE: Record<CreateKind, string> = {
  inquiry: ROUTES.inbox,
  contact: ROUTES.contacts,
  company: ROUTES.companies,
  task: ROUTES.tasks,
  // A view is a saved QUERY, so it is made where the query is: the inbox opens
  // the Save view dialog. `/settings/views` lists and orders them; it cannot
  // mint one, and its own "New view" button says so by coming here too.
  view: ROUTES.inbox,
};

/** `true` when a screen claimed it. */
function announceCreate(kind: CreateKind): boolean {
  const event = new CustomEvent<CreateEventDetail>(CREATE_EVENT, {
    detail: { kind },
    cancelable: true,
  });
  return !window.dispatchEvent(event);
}

/**
 * A surface says "I can make this kind" — and the shell's offer becomes real.
 * Without an implementation of this side the contract is decorative.
 */
export function useCreateRequest(
  kind: CreateKind,
  handler: () => void,
  /**
   * Exactly one listener per kind may be mounted at a time — the claim consumes
   * the event — so a screen that renders its create control twice (a toolbar
   * primary and a copy in the empty state) opts the second one out.
   */
  enabled = true,
): void {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!enabled) return;
    const onRequest = (event: Event) => {
      const detail = (event as CustomEvent<CreateEventDetail>).detail;
      if (detail?.kind !== kind) return;
      event.preventDefault();
      latest.current();
    };
    window.addEventListener(CREATE_EVENT, onRequest);
    return () => window.removeEventListener(CREATE_EVENT, onRequest);
  }, [kind, enabled]);
}

/**
 * The caller's half: ask for a create from any door in the product.
 *
 * Claimed here → the composer opens where you stand. Unclaimed → the request is
 * parked and the caller navigates to the surface that owns the kind; the SHELL
 * then re-offers it (see `useCreateHost`).
 *
 * The park has to be module state, not this hook's state. The doors are
 * components that DIE in the navigation they cause — the Saved-views pane
 * unmounts the instant `New view` sends you to the inbox — so a retry owned by
 * the door is torn down before the target screen exists. Measured: the sidebar's
 * `+` (which lives in the shell and survives) opened the Save-view dialog; the
 * pane's own `New view`, one line of identical code away, navigated and stopped.
 */
let parked: CreateKind | null = null;
const PARK_EVENT = 'af:create-parked';

export function useCreate(): (kind: CreateKind) => void {
  const navigate = useNavigate();

  return useCallback(
    (kind: CreateKind) => {
      if (announceCreate(kind)) return;
      parked = kind;
      window.dispatchEvent(new CustomEvent(PARK_EVENT));
      navigate(CREATE_ROUTE[kind]);
    },
    [navigate],
  );
}

/**
 * Mounted ONCE, by the shell, which outlives every navigation.
 *
 * Re-offers a parked create as soon as the owning surface is on screen — and
 * keeps offering for a moment, because a feature screen is code-split:
 * `location.pathname` flips the instant the router navigates, several hundred
 * milliseconds before the lazy chunk has downloaded and mounted its listener. A
 * single announce on the next tick landed in an empty room whenever the target
 * screen had not been visited yet, which is exactly the cold-load case a
 * stranger hits.
 *
 * Bounded and self-cancelling: it stops the moment a screen claims the offer and
 * gives up after RETRY_MS either way. A create request must never survive into a
 * later navigation.
 */
export function useCreateHost(): void {
  const location = useLocation();
  const [version, bump] = useState(0);

  useEffect(() => {
    const onPark = () => bump((n) => n + 1);
    window.addEventListener(PARK_EVENT, onPark);
    return () => window.removeEventListener(PARK_EVENT, onPark);
  }, []);

  useEffect(() => {
    const kind = parked;
    if (!kind) return;
    if (location.pathname !== CREATE_ROUTE[kind]) return;

    let elapsed = 0;
    let timer = 0;
    const tick = () => {
      if (announceCreate(kind)) {
        parked = null;
        return;
      }
      elapsed += RETRY_STEP_MS;
      if (elapsed >= RETRY_MS) {
        parked = null;
        return;
      }
      timer = window.setTimeout(tick, RETRY_STEP_MS);
    };
    timer = window.setTimeout(tick, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname, version]);
}
